import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { wishlistService } from '../services/wishlistService';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

const WishlistContext = createContext(null);

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlistItems([]);
      setWishlistIds(new Set());
      return;
    }

    setLoading(true);
    try {
      const data = await wishlistService.getWishlist();
      setWishlistItems(data);
      setWishlistIds(new Set(data.map(item => item.product_id)));
    } catch (error) {
      console.error('Failed to fetch wishlist', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const toggleWishlist = async (product) => {
    if (!isAuthenticated) {
      toast.info('Please login to add items to wishlist');
      return;
    }

    try {
      const result = await wishlistService.toggleWishlist(product.id);
      
      if (result.status === 'added') {
        setWishlistIds(prev => new Set(prev).add(product.id));
        setWishlistItems(prev => [...prev, { product_id: product.id, product }]);
        toast.success(`${product.name} added to wishlist`);
      } else {
        setWishlistIds(prev => {
          const next = new Set(prev);
          next.delete(product.id);
          return next;
        });
        setWishlistItems(prev => prev.filter(item => item.product_id !== product.id));
        toast.info('Removed from wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const isInWishlist = (productId) => wishlistIds.has(productId);

  const value = {
    wishlistItems,
    wishlistIds,
    loading,
    toggleWishlist,
    isInWishlist,
    refreshWishlist: fetchWishlist
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};
